import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { Stack } from "@/core/components/ui/stack";
import { cn } from "@/core/utils/utils";

const markdownComponents: Components = {
  h1: ({ children }) => (
    <h1 className="mb-3 mt-6 text-2xl font-bold text-primary-text first:mt-0">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="mb-2 mt-6 text-lg font-semibold text-primary-text first:mt-0">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="mb-2 mt-4 text-base font-semibold text-primary-text first:mt-0">
      {children}
    </h3>
  ),
  h4: ({ children }) => (
    <h4 className="mb-1 mt-3 text-sm font-semibold text-primary-text first:mt-0">
      {children}
    </h4>
  ),
  p: ({ children }) => (
    <p className="mb-3 text-sm leading-relaxed text-primary-text last:mb-0">
      {children}
    </p>
  ),
  ul: ({ children }) => (
    <ul className="mb-3 ml-4 list-disc space-y-1 text-sm text-primary-text last:mb-0">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="mb-3 ml-4 list-decimal space-y-1 text-sm text-primary-text last:mb-0">
      {children}
    </ol>
  ),
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => (
    <strong className="font-semibold text-primary-text">{children}</strong>
  ),
  em: ({ children }) => (
    <em className="italic text-secondary-text">{children}</em>
  ),
  pre: ({ children }) => (
    <pre className="mb-3 overflow-x-auto rounded-lg bg-bg-secondary px-4 py-3 font-mono text-xs text-primary-text">
      {children}
    </pre>
  ),
  code: ({ className, children }) => {
    const isBlock = className?.startsWith("language-");
    return isBlock ? (
      <code className={className}>{children}</code>
    ) : (
      <code className="rounded bg-bg-secondary px-1.5 py-0.5 font-mono text-xs text-primary-text">
        {children}
      </code>
    );
  },
  blockquote: ({ children }) => (
    <blockquote className="mb-3 border-l-2 border-stroke-secondary pl-4 text-sm text-secondary-text">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-4 border-stroke-tertiary" />,
  table: ({ children }) => (
    <Stack fullWidth gap="none" className="mb-3 overflow-x-auto">
      <table className="w-full border-collapse text-sm">{children}</table>
    </Stack>
  ),
  thead: ({ children }) => (
    <thead className="border-b border-stroke-secondary bg-bg-secondary">
      {children}
    </thead>
  ),
  th: ({ children }) => (
    <th className="px-3 py-2 text-left text-xs font-semibold text-secondary-text">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border-b border-stroke-tertiary px-3 py-2 text-primary-text">
      {children}
    </td>
  ),
};

interface PayPlanMarkdownProps {
  document: string;
  className?: string;
}

export const PayPlanMarkdown = ({
  document,
  className,
}: PayPlanMarkdownProps) => (
  <Stack fullWidth gap="none" className={cn("min-w-0", className)}>
    <ReactMarkdown components={markdownComponents} remarkPlugins={[remarkGfm]}>
      {document}
    </ReactMarkdown>
  </Stack>
);
